import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Components
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import OTPVerification from './components/Auth/OTPVerification';
import AdminLogin from './components/Auth/AdminLogin';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Impersonate from './components/Auth/Impersonate';
import UserDashboard from './components/Dashboard/UserDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import AdminAnalytics from './components/Admin/AdminAnalytics';
import AdminFeatures from './components/Admin/AdminFeatures';
import AdminWallet from './components/Admin/AdminWallet';
import AdminSettings from './components/Admin/AdminSettings';
import AdminAdmins from './components/Admin/AdminAdmins';
import ProtectedRoute from './components/Common/ProtectedRoute';

// Styles
import './styles/globals.css';
import './styles/auth.css';
import './styles/dashboard.css';
import 'react-toastify/dist/ReactToastify.css';
import Campaigns from './components/Features/Campaigns';
import Contacts from './components/Features/Contacts';
import Templates from './components/Features/Templates';
import Automation from './components/Features/Automation';
import Analytics from './components/Features/Analytics';
import ConnectWhatsApp from './components/Features/ConnectWhatsApp';
import Chat from './components/Features/Chat';
import Settings from './components/Features/Settings';
import Wallet from './components/Features/Wallet';
import WhatsAppCallback from './components/Features/WhatsAppCallback';
import BusinessInsights from './components/Features/BusinessInsights';
import WhatsAppFlows from './components/Features/WhatsAppFlows';
import CTWAAdManager from './components/Features/CTWAAdManager';
import MediaManager from './components/Features/MediaManager';
import Reports from './components/Features/Reports';

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <AdminProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-otp" element={<OTPVerification />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/impersonate" element={<Impersonate />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Protected User Routes */}
                <Route
                  path="/connect-whatsapp"
                  element={
                    <ProtectedRoute>
                      <ConnectWhatsApp />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <UserDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/chat"
                  element={
                    <ProtectedRoute>
                      <Chat />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns"
                  element={
                    <ProtectedRoute>
                      <Campaigns />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute>
                      <Contacts />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute>
                      <Templates />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/automation"
                  element={
                    <ProtectedRoute>
                      <Automation />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/wallet"
                  element={
                    <ProtectedRoute>
                      <Wallet />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/insights"
                  element={
                    <ProtectedRoute>
                      <BusinessInsights />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/flows/whatsapp"
                  element={
                    <ProtectedRoute>
                      <WhatsAppFlows />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ads/ctwa"
                  element={
                    <ProtectedRoute>
                      <CTWAAdManager />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/media-library"
                  element={
                    <ProtectedRoute>
                      <MediaManager />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/whatsapp-callback"
                  element={
                    <ProtectedRoute>
                      <WhatsAppCallback />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Admin Routes */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/features"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminFeatures />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/wallet"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminWallet />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/admins"
                  element={
                    <ProtectedRoute adminRoute>
                      <AdminAdmins />
                    </ProtectedRoute>
                  }
                />

                {/* Default Redirects */}
                {/* <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/admin" element={<Navigate to="/admin/login" replace />} /> */}

                {/* 404 Route */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>

              {/* Toast Notifications */}
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
            </div>
          </Router>
        </AdminProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;